import { NavPublic } from "@/components/NavPublic";

export function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      <NavPublic />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
  <h1 className="page-title text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        
        <p className="text-sm text-gray-600 mb-8">Last updated: October 3, 2025</p>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms of Service ("Terms") constitute a legally binding agreement between Smart Care Audits ("we", "us", or "our") and you (the "Customer" or "you") governing your use of our compliance management platform for UK care homes.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By accessing or using our platform, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, you must not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
            <p className="text-gray-700 leading-relaxed">
              Smart Care Audits provides a software-as-a-service (SaaS) platform that enables UK care homes to:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Create, manage, and complete compliance audits</li>
              <li>Import and customize audit templates</li>
              <li>Generate compliance reports and documentation</li>
              <li>Track due dates and receive notifications</li>
              <li>Manage multi-home organizational structures</li>
              <li>Control user access with role-based permissions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts and Registration</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.1 Account Creation</h3>
            <p className="text-gray-700 leading-relaxed">
              To use our platform, you must create an account or be granted access by your organization administrator. You must provide accurate, current, and complete information during registration.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Account Security</h3>
            <p className="text-gray-700 leading-relaxed">You are responsible for:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access or security breach</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.3 Eligibility</h3>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 18 years old and have the authority to enter into these Terms on behalf of your organization to use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. License and Permitted Use</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.1 License Grant</h3>
            <p className="text-gray-700 leading-relaxed">
              Subject to these Terms and payment of applicable fees, we grant you a non-exclusive, non-transferable, revocable license to access and use our platform for your internal business purposes in compliance with UK care home regulations.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.2 Restrictions</h3>
            <p className="text-gray-700 leading-relaxed">You must not:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Copy, modify, or create derivative works of the platform</li>
              <li>Reverse engineer, decompile, or disassemble any part of the software</li>
              <li>Resell, sublicense, or provide access to third parties</li>
              <li>Remove or alter any proprietary notices or labels</li>
              <li>Use the platform for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to any systems or data</li>
              <li>Interfere with or disrupt the platform's operation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Subscription and Payments</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.1 Subscription Plans</h3>
            <p className="text-gray-700 leading-relaxed">
              Our platform is offered through tiered subscription plans with pricing based on the number of care homes and users. Specific pricing is provided during the sales process and detailed in your subscription agreement.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.2 Payment Terms</h3>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Subscriptions are billed monthly or annually in advance</li>
              <li>Payment is processed via bank transfer or direct debit as agreed</li>
              <li>All fees are exclusive of VAT, which will be added where applicable</li>
              <li>Invoices are due within 30 days of issue unless otherwise agreed</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.3 Late Payments</h3>
            <p className="text-gray-700 leading-relaxed">
              Late payments may incur interest at a rate of 4% above the Bank of England base rate. We reserve the right to suspend access for non-payment after 14 days written notice.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.4 Refunds</h3>
            <p className="text-gray-700 leading-relaxed">
              For consumer customers: You have a 14-day cancellation right from the date of purchase. For business customers: Refunds are provided at our discretion as outlined in your subscription agreement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Protection and Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              We process personal data in accordance with our Privacy Policy and UK GDPR requirements. As a data processor, we will:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Process data only on your documented instructions</li>
              <li>Implement appropriate security measures</li>
              <li>Assist with data subject requests and regulatory compliance</li>
              <li>Notify you of any data breaches within 24 hours</li>
              <li>Return or delete data upon contract termination as requested</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              A separate Data Processing Agreement (DPA) is available upon request for business customers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property Rights</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.1 Our IP</h3>
            <p className="text-gray-700 leading-relaxed">
              All rights, title, and interest in the platform, including software, design, content, trademarks, and documentation, are and remain our exclusive property. No ownership rights are transferred to you.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.2 Your Data</h3>
            <p className="text-gray-700 leading-relaxed">
              You retain all ownership rights to the data you upload to the platform ("Customer Data"). You grant us a limited license to process Customer Data solely to provide our services.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.3 Templates</h3>
            <p className="text-gray-700 leading-relaxed">
              Master templates provided by us remain our intellectual property. Customized templates based on our templates are licensed to you for your use within the platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Service Availability and Support</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">8.1 Uptime</h3>
            <p className="text-gray-700 leading-relaxed">
              We strive to maintain 99.5% platform availability, excluding scheduled maintenance. We do not guarantee uninterrupted access and are not liable for downtime beyond our reasonable control.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">8.2 Maintenance</h3>
            <p className="text-gray-700 leading-relaxed">
              We may perform scheduled maintenance with advance notice. Emergency maintenance may occur without notice to address security or critical issues.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">8.3 Support</h3>
            <p className="text-gray-700 leading-relaxed">
              Email support is provided during UK business hours (9am-5pm, Monday-Friday). Priority support with guaranteed response times is available on higher-tier plans.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Warranties and Disclaimers</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">9.1 Our Warranties</h3>
            <p className="text-gray-700 leading-relaxed">We warrant that our platform will:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Be provided with reasonable skill and care</li>
              <li>Substantially conform to the description in our documentation</li>
              <li>Comply with applicable UK data protection laws</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">9.2 Disclaimers</h3>
            <p className="text-gray-700 leading-relaxed">
              Our platform is provided "as is" for business purposes. While we strive for accuracy, we do not warrant that:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>The platform will meet all your specific requirements</li>
              <li>The platform will be error-free or uninterrupted</li>
              <li>All defects will be corrected immediately</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Important:</strong> Our platform assists with compliance management but does not constitute legal or regulatory advice. You remain responsible for ensuring compliance with CQC, NHS, and other regulatory requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">10.1 What We Are Not Liable For (B2B Customers)</h3>
            <p className="text-gray-700 leading-relaxed">To the maximum extent permitted by law, we are not liable for:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Loss of profits, revenue, business, or anticipated savings</li>
              <li>Loss of data (ensure you maintain backups)</li>
              <li>Loss of goodwill or reputation</li>
              <li>Indirect, consequential, or special damages</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">10.2 Liability Cap (B2B Customers)</h3>
            <p className="text-gray-700 leading-relaxed">
              Our total liability for any claims arising from these Terms is limited to the fees paid by you in the 12 months preceding the claim.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">10.3 Exceptions</h3>
            <p className="text-gray-700 leading-relaxed">Nothing in these Terms excludes or limits our liability for:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Death or personal injury caused by our negligence</li>
              <li>Fraud or fraudulent misrepresentation</li>
              <li>Breach of statutory rights under the Consumer Rights Act 2015 (for consumer customers)</li>
              <li>Any other liability that cannot be excluded under UK law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">11.1 Termination by You</h3>
            <p className="text-gray-700 leading-relaxed">
              You may cancel your subscription at any time by providing 30 days written notice. No refunds are provided for partial months unless required by consumer protection law.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">11.2 Termination by Us</h3>
            <p className="text-gray-700 leading-relaxed">We may terminate your access immediately if you:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Breach these Terms and fail to remedy within 14 days of notice</li>
              <li>Fail to pay fees after 30 days of invoice due date</li>
              <li>Use the platform for unlawful purposes</li>
              <li>Compromise the security or integrity of our systems</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">11.3 Effect of Termination</h3>
            <p className="text-gray-700 leading-relaxed">Upon termination:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Your access to the platform will cease immediately</li>
              <li>You must pay any outstanding fees</li>
              <li>We will provide a data export in CSV/JSON format within 30 days if requested</li>
              <li>We will delete your data within 90 days unless legally required to retain it</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may modify these Terms from time to time. We will notify you of material changes by:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-700">
              <li>Email notification at least 30 days before changes take effect</li>
              <li>Posting updated Terms on our platform with a new "Last Updated" date</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Continued use after the effective date constitutes acceptance of the new Terms. If you do not agree, you must terminate your subscription.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law and Jurisdiction</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of England and Wales. Any disputes arising from these Terms will be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              If you are a consumer, you may also have the right to bring proceedings in the courts of Scotland or Northern Ireland if you are resident there.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. General Provisions</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">14.1 Entire Agreement</h3>
            <p className="text-gray-700 leading-relaxed">
              These Terms, together with our Privacy Policy and any subscription agreement, constitute the entire agreement between you and us.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">14.2 Assignment</h3>
            <p className="text-gray-700 leading-relaxed">
              You may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights to any affiliate or successor.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">14.3 Severability</h3>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">14.4 Waiver</h3>
            <p className="text-gray-700 leading-relaxed">
              Our failure to enforce any right or provision does not constitute a waiver of that right or provision.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions about these Terms, please contact us:
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Smart Care Audits</strong><br />
              <strong>Email:</strong> info@smartcareaudits.co.uk<br />
              <strong>Website:</strong> smartcareaudits.co.uk
            </p>
          </section>
        </div>
      </div>

      <footer className="bg-gray-900 text-gray-300 py-8 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-4xl mx-auto text-center text-sm">
          <p>&copy; {new Date().getFullYear()} Smart Care Audits. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
